import axios from "axios";
import { message } from "antd";

const API_ENDPOINT = process.env.REACT_APP_HOMEWORK_SERVER_API_ENDPOINT;

export interface CreateCouponPayload {
    code: string;
    type: "FLAT_OFF" | "PERCENTAGE_OFF";
    value: number;
    validFrom: string;
    validTill: string;
    userType: "ALL_USERS" | "LIST_OF_USERS";
    listOfUsers?: string[];
    regions: string[];
    plans: string[];
}

export interface CreateCouponResponse {
    success: boolean;
    data?: any;
    message?: string;
}

/**
 * Create a new coupon
 * @param payload - Coupon creation data
 * @returns Promise with API response
 */
export const createCoupon = async (
    payload: CreateCouponPayload,
): Promise<CreateCouponResponse> => {
    try {
        const response = await axios.post(
            `${API_ENDPOINT}/v2/coupon/create`,
            {
                data: payload,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );

        if (response.data.success) {
            message.success("Coupon created successfully!");
            return response.data;
        } else {
            message.error(response.data.message || "Failed to create coupon");
            return response.data;
        }
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to create coupon";
        message.error(errorMessage);
        throw error;
    }
};

/**
 * Check if coupon code already exists
 * @param code - Coupon code to check
 * @returns Promise with check result
 */
export const checkCouponCode = async (
    code: string,
): Promise<{ exists: boolean; message?: string }> => {
    try {
        const response = await axios.post(
            `${API_ENDPOINT}/v2/coupon/check`,
            {
                data: { code },
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );

        return {
            exists: response.data.exists || false,
            message: response.data.message,
        };
    } catch (error: any) {
        console.error("Error checking coupon code:", error);
        // If API fails, assume code doesn't exist to allow form submission
        return { exists: false };
    }
};

/**
 * Delete a coupon by code
 * @param code - Coupon code to delete
 * @returns Promise with API response
 */
export const deleteCoupon = async (
    code: string,
): Promise<CreateCouponResponse> => {
    try {
        const response = await axios.post(
            `${API_ENDPOINT}/v2/coupon/delete`,
            {
                data: {
                    code: code,
                },
            },
            {
                headers: {
                    "Content-Type": "application/json",
                },
            },
        );

        if (response.data.success) {
            message.success("Coupon deleted successfully!");
            return response.data;
        } else {
            message.error(response.data.message || "Failed to delete coupon");
            return response.data;
        }
    } catch (error: any) {
        const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "Failed to delete coupon";
        message.error(errorMessage);
        throw error;
    }
};

/**
 * Parse CSV file to extract profile IDs
 * @param file - CSV file
 * @returns Promise with array of profile IDs
 */
export const parseCSVForProfileIds = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const text = e.target?.result as string;
                const lines = text.trim().split(/\r?\n/);

                if (lines.length === 0) {
                    reject(new Error("CSV file is empty"));
                    return;
                }

                // Parse header
                const header = lines[0]
                    .split(",")
                    .map((h) => h.trim().toLowerCase());
                const profileIdIndex = header.findIndex(
                    (h) => h === "profile_id" || h === "profileid",
                );

                if (profileIdIndex === -1) {
                    reject(
                        new Error(
                            'CSV must have a "profile_id" column in the header',
                        ),
                    );
                    return;
                }

                // Parse data rows
                const profileIds: string[] = [];
                for (let i = 1; i < lines.length; i++) {
                    const row = lines[i].split(",");
                    const profileId = row[profileIdIndex]?.trim();
                    if (profileId) {
                        profileIds.push(profileId);
                    }
                }

                if (profileIds.length === 0) {
                    reject(new Error("No valid profile IDs found in CSV"));
                    return;
                }

                resolve(profileIds);
            } catch (error) {
                reject(new Error("Failed to parse CSV file"));
            }
        };

        reader.onerror = () => {
            reject(new Error("Failed to read CSV file"));
        };

        reader.readAsText(file);
    });
};
